import { Component } from '@theme/component';

/**
 * Announcement banner custom element that crossfades between slides.
 *
 * Owns slide lifecycle for the bar: rotation, initial visibility, and the
 * removal of expired countdown slides (countdown-timer.js only *announces*
 * expiry via the `announcement:expired` event — this component decides what
 * happens to the slide and the bar).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    // Pause while a keyboard user is focused inside the bar so a CTA can't fade
    // out from under them — also serves as a WCAG 2.2.2 pause mechanism.
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    this.addEventListener('announcement:expired', this.#handleExpired);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Nothing rendered (e.g. the only block was an already-expired countdown, or all blocks fell
    // outside their schedule window) — hide the empty padded bar instead of leaving a blank band.
    if ((this.refs.slides ?? []).length === 0) {
      this.#hideBar();
      return;
    }

    // Establish initial slide visibility (aria-hidden on all but the first),
    // matching the CSS :first-child fallback used before hydration.
    this.current = 0;

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic playback. No-op when autoplay is off or the visitor
   * prefers reduced motion.
   * @param {number} [interval] - The time interval in ms between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.prefersReducedMotion) return;

    // Guarantee a single running interval.
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Stops automatic playback (used by hover, focus and tab-hidden).
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic playback if eligible.
   */
  resume() {
    this.play();
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    this.#current = current;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    slides.forEach((slide, index) => {
      const hidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${hidden}`);
      // Mirror `inert` so focusable content (CTA links) inside an off-screen,
      // aria-hidden slide leaves the tab order and the a11y tree (WCAG 4.1.2).
      // Matches the hero-slider active-slide pattern (hero-slide-active.js).
      slide.toggleAttribute('inert', hidden);
    });
  }

  /**
   * Handles an expired countdown: removes its slide and re-evaluates the bar.
   * @param {Event} event
   */
  #handleExpired = (event) => {
    const target = /** @type {Element} */ (event.target);
    const expiredSlide = target.closest('slideshow-slide');
    expiredSlide?.remove();

    // Direct query rather than refs.slides: refs is MutationObserver-backed and
    // has not re-queried the just-removed node yet at this point.
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));

    if (slides.length === 0) {
      this.#hideBar();
      return;
    }

    // The removed slide may have been the visible one — show the first survivor.
    slides.forEach((slide, index) => {
      const hidden = index !== 0;
      slide.setAttribute('aria-hidden', `${hidden}`);
      slide.toggleAttribute('inert', hidden);
    });
    this.#current = 0;

    if (slides.length <= 1) {
      this.suspend();
      this.querySelector('slideshow-arrows')?.setAttribute('hidden', '');
    }
  };

  /**
   * Hides the whole bar (and its full-width background) when no slides remain.
   */
  #hideBar() {
    const bar = this.closest('.announcement-bar');
    if (!bar) return;

    bar.setAttribute('hidden', '');

    const background = bar.previousElementSibling;
    if (background?.classList.contains('section-background')) {
      background.setAttribute('hidden', '');
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
